"use client";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { connect } from "react-redux";
import * as packagesActions from "store/packages/actions";
import { getVendor, replaceOnlyNumber, slugfy } from "common/utils";
import { StepItem } from "./StepItem";
import { StatItem } from "./StatItem";
import { InfoItem } from "./InfoItem";
import LoadingPlanCards from "./LoadingPlanCards";

export function HealthcarePlansSection({
  packagesRequest,
  packagesState,
  auth_state,
  vendors,
}) {
  const isFirstRender = useRef(true);
  const { search } = useLocation();
  const [packages, setPackages] = useState([]);
  const [vendor, setVendor] = useState(null);

  const featuresPlans = [
    {
      id: 5,
      plan: "Individual",
      items:
        [
          "Telemedicina 24h",
          "Consultas e exames com desconto",
          "Seguros inclusos",
          "Acesso ao Clube de Vantagens",
        ]
    },
    {
      id: 6,
      plan: "Familiar",
      items:
        [
          "Todos os benefícios",
          "+ Inclusão de dependentes",
          "+ Suporte saúde emocional e familiar",
          "+ Suporte saúde pet"
        ]
    },

  ]

  // Configuração do vendor baseada na URL
  useMemo(() => {
    if (search && !!vendors.length) {
      setVendor(getVendor(replaceOnlyNumber(search), vendors));
    }
  }, [search, vendors]);

  // Atualização dos pacotes quando carregados
  useEffect(() => {
    if (!packagesState.loading && !!packagesState.packages.length) {
      setPackages(packagesState.packages);
    }
  }, [packagesState.loading, packagesState.packages]);

  // Requisição inicial dos pacotes
  useEffect(() => {
    if (
      isFirstRender.current ||
      (packagesState.packages.length === 0 &&
        (auth_state.success || auth_state.tokenVerify.success))
    ) {
      isFirstRender.current = false;
      packagesRequest();
    }
  }, [
    packagesRequest,
    packagesState.packages.length,
    auth_state.success,
    auth_state.tokenVerify.success,
  ]);

  // Função para determinar se é plano individual
  const isIndividualPlan = (name) => {
    const regexIndividual = /\bindividual\b/i;
    return regexIndividual.test(name);
  };

  // Função para gerar props do plano baseado nos dados dinâmicos
  const generatePlanProps = (packageData) => {
    const isIndividual = isIndividualPlan(packageData.name);

    // Filtra apenas os recursos incluídos
    const includedFeatures = packageData.package_details
      .filter(detail => detail.included)
      .map(detail => detail.description);

    return {
      icon: isIndividual ? individualPlanIcon : familyPlanIcon,
      title: isIndividual ?
        "Plano Individual" :
        (<>Plano Familiar<small className="d-block">(até 3 dependentes)</small></>),
      features: includedFeatures,
      price: `R$ ${packageData.price.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        currency: "BRL",
      })}`,
      packageData: {
        ...packageData,
        vendor_cpf: vendor ? vendor.cpf : "",
        vendor_user_id: vendor ? `${vendor.id}` : "",
        vendor_name: vendor ? vendor.name : "",
      }
    };
  };

  const stepItems = [
    {
      icon: `<svg width="24" height="34" viewBox="0 0 24 34" fill="none" xmlns="http://www.w3.org/2000/svg" class="step-icon" style="position: relative"> <g clip-path="url(#clip0_66_670)"> <path d="M15.64 5.39506C15.64 2.65506 13.42 0.435059 10.68 0.435059C7.93997 0.435059 5.71997 2.65506 5.71997 5.39506C5.71997 7.25506 6.73997 8.87506 8.25997 9.72506L8.35997 6.43506C8.35997 5.08506 9.44997 3.99506 10.8 3.99506H10.94C12 3.99506 12.86 4.85506 12.86 5.91506V9.84506C14.5 9.03506 15.64 7.35506 15.64 5.39506Z" fill="#AAD4C8"></path> <path d="M12.8701 5.91512C12.8701 4.85512 12.0101 3.99512 10.9501 3.99512H10.8101C9.46007 3.99512 8.37007 5.08512 8.37007 6.43512L8.27007 9.72512L8.04007 16.9751C7.99007 17.1951 7.96007 22.2351 7.96007 22.2351C7.54007 21.8051 7.06007 21.3051 6.63007 20.8251C5.63007 19.7151 3.81007 18.9651 2.59007 18.8251C1.95007 18.7551 1.46007 18.8351 1.14007 19.0851C1.03007 19.1651 0.93007 19.2551 0.84007 19.3451C0.22007 19.9651 -0.25993 20.9651 0.18007 21.6951C0.37007 22.0151 0.70007 22.2251 1.04007 22.4451C1.50007 22.7451 2.27007 23.0651 2.85007 23.8051C3.82007 25.0451 6.07007 27.8251 7.45007 29.3051C7.74007 29.4851 8.34007 30.0951 8.63007 30.2451C9.50007 30.8051 10.2201 31.3251 10.4601 31.6151C10.6501 31.8351 11.7901 32.4251 11.7901 32.4251C12.7101 33.0151 14.3901 33.5551 16.3301 33.5551C18.2701 33.5551 20.0301 33.6751 21.3301 32.5251H21.4001L21.4301 32.4351C22.7601 31.2251 23.5801 29.5451 23.5801 27.6851V18.8051C23.5801 18.3651 23.4801 16.9151 22.2601 16.4751C21.6701 16.2651 21.1101 16.3451 20.6401 16.5451H20.5301H20.5001C20.2901 15.7451 19.7601 15.2251 18.9701 15.0851C18.0201 14.9251 17.3601 15.2051 16.9601 15.4951C16.5701 14.6451 15.7701 14.2051 14.7001 14.2951C13.9001 14.3651 13.3001 14.7551 12.8701 15.2751V5.91512Z" fill="#616E7D"></path> </g> <defs> <clipPath id="clip0_66_670"> <rect width="23.58" height="33.13" fill="white" transform="translate(0 0.435059)"></rect> </clipPath> </defs> </svg>`,
      text: "Escolha o plano mais adequado para você"
    },
    {
      icon: `<svg width="23" height="36" viewBox="0 0 23 36" fill="none" xmlns="http://www.w3.org/2000/svg" class="step-icon" style="position: relative"> <g clip-path="url(#clip0_66_675)"> <path d="M2.64008 0.814941C1.50008 0.814941 0.580078 1.73494 0.580078 2.87494V33.1249C0.580078 34.2649 1.50008 35.1849 2.64008 35.1849H20.3701C21.5101 35.1849 22.4301 34.2649 22.4301 33.1249V2.87494C22.4301 1.73494 21.5101 0.814941 20.3701 0.814941H2.64008ZM11.5001 33.1849C10.8001 33.1849 10.2401 32.6249 10.2401 31.9249C10.2401 31.2249 10.8001 30.6649 11.5001 30.6649C12.2001 30.6649 12.7601 31.2249 12.7601 31.9249C12.7601 32.6249 12.2001 33.1849 11.5001 33.1849ZM19.2001 29.2149H3.81008V3.65494H19.2001V29.2149Z" fill="#616E7D"></path> <path d="M10.24 31.9249C10.24 32.6249 10.8 33.1849 11.5 33.1849C12.2 33.1849 12.76 32.6249 12.76 31.9249C12.76 31.2249 12.2 30.6649 11.5 30.6649C10.8 30.6649 10.24 31.2249 10.24 31.9249Z" fill="white"></path> <path d="M17.52 5.25488H5.52002V14.4449H17.52V5.25488Z" fill="#AAD4C8"></path> <path d="M17.52 15.3849H5.52002V20.7949H17.52V15.3849Z" fill="#82BFAB"></path> <path d="M17.52 21.3849H5.52002V26.7949H17.52V21.3849Z" fill="#82BFAB"></path> </g> <defs> <clipPath id="clip0_66_675"> <rect width="21.85" height="34.37" fill="white" transform="translate(0.580078 0.814941)"></rect> </clipPath> </defs> </svg>`,
      text: "Acesse os serviços via app ou site"
    },
    {
      icon: `<svg width="39" height="30" viewBox="0 0 39 30" fill="none" xmlns="http://www.w3.org/2000/svg" class="step-icon" style="position: relative"> <g clip-path="url(#clip0_66_683)"> <path d="M34.5102 2.26001H12.4902L14.2002 6.52001L34.2802 6.54001C34.2802 6.54001 35.0102 6.58001 35.4102 6.67001H35.3702V3.12001C35.3702 2.65001 34.9802 2.26001 34.5102 2.26001Z" fill="#AAD4C8"></path> <path d="M35.4102 6.66995C35.0102 6.57995 34.2802 6.53995 34.2802 6.53995L14.2002 6.51995L12.4902 2.25995L11.8602 0.699951H2.25018C1.25018 0.699951 0.430176 1.51995 0.430176 2.51995V27.47C0.430176 28.48 1.25018 29.3 2.26018 29.3H36.3602C37.3702 29.3 38.1902 28.48 38.1902 27.47V10.31C38.1902 8.57995 37.0202 7.11995 35.4202 6.66995H35.4102ZM26.1402 19.95H21.3702V24.72H17.2402V19.95H12.4702V15.82H17.2402V11.05H21.3702V15.82H26.1402V19.95Z" fill="#616E7D"></path> <path d="M21.3702 24.7199V19.9499H26.1402V15.8199H21.3702V11.0499H17.2402V15.8199H12.4702V19.9499H17.2402V24.7199H21.3702Z" fill="white"></path> </g> <defs> <clipPath id="clip0_66_683"> <rect width="37.75" height="28.6" fill="white" transform="translate(0.430176 0.699951)"></rect> </clipPath> </defs> </svg>`,
      text: "Use quando precisar: consulta, exame, farmácia, suporte, telemedicina"
    },
    {
      icon: `<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" class="step-icon" style="position: relative"> <g clip-path="url(#clip0_66_689)"> <path d="M0.180054 14C0.180054 21.45 6.22005 27.48 13.6601 27.48C21.1001 27.48 27.1401 21.44 27.1401 14C27.1401 6.56002 21.1101 0.52002 13.6601 0.52002C6.21005 0.52002 0.180054 6.56002 0.180054 14ZM25.8101 14C25.8101 20.71 20.3701 26.15 13.6601 26.15C6.95005 26.15 1.51005 20.71 1.51005 14C1.51005 7.29002 6.95005 1.85002 13.6601 1.85002C20.3701 1.85002 25.8101 7.29002 25.8101 14Z" fill="#82BFAB"></path> <path d="M3.41003 14C3.41003 19.66 8.01003 24.26 13.67 24.26C19.33 24.26 23.93 19.66 23.93 14C23.93 8.34 19.32 3.75 13.66 3.75C8.00003 3.75 3.41003 8.35 3.41003 14ZM14.26 7.84V14.33L19.29 19.55C19.58 19.85 19.57 20.33 19.27 20.62C18.97 20.91 18.49 20.9 18.2 20.6L12.96 15.16C12.82 15.02 12.75 14.83 12.75 14.64V7.85C12.75 7.43 13.09 7.1 13.5 7.1C13.91 7.1 14.25 7.44 14.25 7.85L14.26 7.84Z" fill="#A4B3C1"></path> <path d="M1.51001 14C1.51001 20.71 6.95001 26.15 13.66 26.15C20.37 26.15 25.81 20.71 25.81 14C25.81 7.28998 20.37 1.84998 13.66 1.84998C6.95001 1.84998 1.51001 7.28998 1.51001 14ZM23.92 14C23.92 19.66 19.32 24.26 13.66 24.26C8.00001 24.26 3.41001 19.66 3.41001 14C3.41001 8.33998 8.01001 3.74998 13.66 3.74998C19.31 3.74998 23.92 8.34998 23.92 14.01V14Z" fill="#616E7D"></path> <path d="M13.5 7.09008C13.08 7.09008 12.75 7.43008 12.75 7.84008V14.6301C12.75 14.8301 12.83 15.0101 12.96 15.1501L18.2 20.5901C18.49 20.8901 18.97 20.9001 19.27 20.6101C19.57 20.3201 19.58 19.8401 19.29 19.5401L14.26 14.3201V7.83008C14.26 7.41008 13.92 7.08008 13.51 7.08008L13.5 7.09008Z" fill="#616E7D"></path> </g> <defs> <clipPath id="clip0_66_689"> <rect width="26.97" height="26.96" fill="white" transform="translate(0.180054 0.52002)"></rect> </clipPath> </defs> </svg>`,
      text: "Acompanhe tudo em tempo real"
    }
  ];

  const individualPlanIcon = `<svg width="65" height="65" viewBox="0 0 65 65" fill="none" xmlns="http://www.w3.org/2000/svg" class="plan-icon-container" style="display: flex; width: 64px; height: 64px; padding: 20px; justify-content: center; align-items: center; gap: 10px; border-radius: 100px; background: #CCE5D0; position: relative"> <rect x="0.5" y="0.5" width="64" height="64" rx="32" fill="#CCE5D0"></rect> <path d="M32.5 34.2222C37.3325 34.2222 41.25 30.3669 41.25 25.6111C41.25 20.8553 37.3325 17 32.5 17C27.6675 17 23.75 20.8553 23.75 25.6111C23.75 30.3669 27.6675 34.2222 32.5 34.2222ZM32.5 34.2222C36.213 34.2222 39.774 35.6738 42.3995 38.2576C45.025 40.8415 46.5 44.3459 46.5 48M32.5 34.2222C28.787 34.2222 25.226 35.6738 22.6005 38.2576C19.975 40.8415 18.5 44.3459 18.5 48" stroke="#186E2E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path> </svg>`;

  const familyPlanIcon = `<svg width="72" height="71" viewBox="0 0 72 71" fill="none" xmlns="http://www.w3.org/2000/svg" class="plan-icon-container-family" style="display: flex; width: 71px; padding: 20px; justify-content: center; align-items: center; gap: 10px; border-radius: 100px; background: #CCE5D0; position: relative"> <rect x="0.5" width="71" height="71" rx="35.5" fill="#CCE5D0"></rect> <path d="M49.7777 51C49.7777 47.3459 48.3261 43.8415 45.7423 41.2576C43.1585 38.6738 39.654 37.2222 35.9999 37.2222M35.9999 37.2222C32.3459 37.2222 28.8414 38.6738 26.2576 41.2576C23.6738 43.8415 22.2222 47.3459 22.2222 51M35.9999 37.2222C40.7557 37.2222 44.6111 33.3669 44.6111 28.6111C44.6111 23.8553 40.7557 20 35.9999 20C31.2442 20 27.3888 23.8553 27.3888 28.6111C27.3888 33.3669 31.2442 37.2222 35.9999 37.2222Z" stroke="#186E2E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M15.3333 49.2777C15.3333 44.2928 18.4294 39.6629 21.5255 37.4441C20.5078 36.7145 19.694 35.7564 19.1561 34.6547C18.6182 33.5529 18.3729 32.3415 18.4418 31.1275C18.5106 29.9136 18.8916 28.7346 19.551 27.6949C20.2104 26.6552 21.1279 25.7868 22.2222 25.1666" stroke="#186E2E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M56.6665 49.2777C56.6665 44.2928 53.5703 39.6629 50.4742 37.4441C51.4919 36.7145 52.3058 35.7564 52.8436 34.6547C53.3815 33.5529 53.6269 32.3415 53.558 31.1275C53.4891 29.9136 53.1081 28.7346 52.4487 27.6949C51.7893 26.6552 50.8719 25.7868 49.7776 25.1666" stroke="#186E2E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"></path> </svg>`;

  // Componente PlanCard dinâmico que integra com o sistema de roteamento
  const DynamicPlanCard = ({ planProps }) => {
    // Encontra o plano correspondente em featuresPlans pelo id
    const featuresPlan = featuresPlans.find(fp => fp.id === planProps.packageData.id);

    return (
      <div
      className="card h-100 shadow-sm border-0"
      style={{
        borderRadius: '16px',
        maxWidth: '320px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
      }}
      >
      <div className="card-body d-flex flex-column p-4 justify-content-end">
        <div className="text-center mb-3">
        <div
          dangerouslySetInnerHTML={{ __html: planProps.icon }}
          className="mb-3"
        />
        <h3 className="h5 fw-bold mb-0 text-dark" style={{minHeight: '3rem'}}>
          {planProps.title}
        </h3>
        </div>

        <div className="text-muted" style={{ lineHeight: '2', fontSize: '16px' }}>
        {featuresPlan && featuresPlan.items.map((feature, index) => (
          <React.Fragment key={index}>
          {feature}
          {index < featuresPlan.items.length - 1 && <br />}
          </React.Fragment>
        ))}
        </div>

        <div className="text-center mb-3"></div>

        {/* <ul className="list-unstyled flex-grow-1 mb-4">
        {planProps.features.map((feature, index) => (
          <li key={index} className="d-flex align-items-start mb-2">
          <i className="bi bi-check-circle-fill text-success me-2 mt-1 flex-shrink-0"></i>
          <span className="text-muted small">{feature}</span>
          </li>
        ))}
        </ul> */}

        <div className="text-center mb-3">
        <div className="h4 fw-bold text-primary mb-0" >
          {planProps.price}
          <small className="text-muted fw-normal"> /mês</small>
        </div>
        </div>

        <Link
        to={{
          pathname: slugfy(planProps.packageData.name),
        }}
        state={{
          packageId: planProps.packageData.id,
          vendor_cpf: planProps.packageData.vendor_cpf,
          vendor_user_id: planProps.packageData.vendor_user_id,
          vendor_name: planProps.packageData.vendor_name,
        }}
        className="btn btn-primary w-100 rounded-pill fw-bold py-2"
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
          border: 'none'
        }}
        >
        Assinar agora
        </Link>
      </div>
      </div>
    );
  };

  // Filtra apenas os pacotes ativos
  const activePackages = packages.filter(pkg => pkg.active);

  return (
    <div>
      <div className="container-fluid" style={{ paddingTop: '2rem' }}>
        {/* Steps Container - Hidden on mobile */}
        <div className="container-fluid" style={{ paddingTop: '2rem' }}>
          <section className="mb-5 px-3 px-md-0 pb-5 d-none d-md-block">
            <nav className="d-flex justify-content-center align-items-center gap-5 mb-5">
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ maxWidth: 1400 }}>
                <div className="d-flex flex-column flex-lg-row align-items-center justify-content-center gap-4">
                  {stepItems.map((step, index) => (
                    <StepItem key={index} icon={step.icon} text={step.text} />
                  ))}
                </div>
              </div>
            </nav>
          </section>
        </div>

        {/* Stats Container */}
        <section className="mb-5 px-3 px-md-0 pb-5">
          <div className="d-flex flex-column flex-lg-row align-items-center justify-content-center">
            <div className="bg-white rounded-3 shadow-sm p-4" >
              <div className="d-flex flex-column flex-md-row align-items-stretch w-100">
                <div className="stat-col flex-fill d-flex flex-column align-items-center justify-content-center px-3 py-2">
                  <StatItem
                    value="Até 80%"
                    description="de economia em consultas e exames"
                  />
                </div>
                <div className="d-block d-md-none border-top w-100 my-2"></div>
                <div className="d-none d-md-block border-end" style={{ height: 'auto' }}></div>
                <div className="stat-col flex-fill d-flex flex-column align-items-center justify-content-center px-3 py-2">
                  <StatItem
                    value="Até 35%"
                    description="de desconto em medicamentos em várias redes"
                  />
                </div>
                <div className="d-block d-md-none border-top w-100 my-2"></div>
                <div className="d-none d-md-block border-end" style={{ height: 'auto' }}></div>
                <div className="stat-col flex-fill d-flex flex-column align-items-center justify-content-center px-3 py-2">
                  <StatItem
                    value={
                      <>
                        24<span style={{ fontSize: '1.5rem' }}>h</span>
                      </>
                    }
                    description="telemedicina Einstein sem custo adicional"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Plan Cards Container - Agora Dinâmico */}
        <section className="mb-5 px-3 px-md-0 pb-5" id="nossos-planos">
          <div className="d-flex flex-column flex-lg-row align-items-center justify-content-center gap-4">
            {/* Plan Cards - Renderização Dinâmica */}
            <div className="d-flex flex-column align-items-justify justify-content-center">
              {packagesState.loading || activePackages.length === 0 ? (
                <LoadingPlanCards />
              ) : (
                <div className="row g-3 justify-content-center w-100">
                  {activePackages.map((packageData) => {
                    const planProps = generatePlanProps(packageData);
                    return (
                      <div key={packageData.id} className="col-12 col-md-6 col-lg-6 d-flex justify-content-center">
                        <DynamicPlanCard planProps={planProps} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Plans Info Container */}
            <div className="d-flex flex-column align-items-center px-3 px-md-0" style={{ maxWidth: 400 }}>
              <h2 className="h3 fw-bold mb-4 text-center">
                Os melhores planos para você e sua família
              </h2>
              <div className="d-flex flex-column gap-3 mb-4">
                <InfoItem text="Ambos com taxa única de adesão de R$ 30,00" />
                <InfoItem text="Acesso facilitado à saúde mental e bem-estar" />
                <InfoItem text="Descontos em educação, veterinários, odontologia, estética, entre outros" />
                <InfoItem text="Seguros assistenciais e sorteio mensal Porto Seguro inclusos nos planos" />
              </div>
              {/* {activePackages.length > 0 && (
                <div className="d-grid pt-3 w-100">
                  <button
                    className="btn btn-primary btn-lg rounded-pill fw-bold"
                    style={{
                      background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
                      border: 'none',
                      padding: '12px 24px',
                      width: '70%',
                      margin: '0 auto'
                    }}
                  >
                    Contratar agora
                  </button>
                </div>
              )} */}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

// Configuração do Redux
const mapStateToProps = (state) => {
  return {
    packagesState: state.packages.data,
    auth_state: state.auth,
    vendors: state.packages.vendors.users,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    packagesRequest: () => {
      dispatch(packagesActions.packagesRequest());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(HealthcarePlansSection);